/**
 * Input sanitization and validation utilities.
 * Prevents XSS, SQL injection, and other injection attacks.
 */

/**
 * Strip HTML tags and dangerous characters from a string
 */
export function sanitizeText(input: string): string {
  if (typeof input !== 'string') return '';
  return input
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .replace(/data:/gi, '') // Remove data: URIs
    .replace(/vbscript:/gi, '') // Remove vbscript:
    .trim()
    .slice(0, 10000); // Limit length
}

/**
 * Sanitize email address
 */
export function sanitizeEmail(email: string): string {
  if (typeof email !== 'string') return '';
  return email
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9@._+-]/g, '') // Only allow valid email chars
    .slice(0, 254); // RFC 5321 max length
}

/**
 * Sanitize phone number
 */
export function sanitizePhone(phone: string): string {
  if (typeof phone !== 'string') return '';
  return phone
    .replace(/[^0-9+\-() ]/g, '') // Only allow digits and phone chars
    .trim()
    .slice(0, 20);
}

/**
 * Sanitize URL
 */
export function sanitizeUrl(url: string): string {
  if (typeof url !== 'string') return '';
  try {
    const parsed = new URL(url);
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) return '';
    return parsed.toString().slice(0, 2048);
  } catch {
    return '';
  }
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email) && email.length <= 254;
}

/**
 * Validate password strength
 */
export function validatePassword(password: string): { valid: boolean; message?: string } {
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters.' };
  }
  if (password.length > 128) {
    return { valid: false, message: 'Password is too long.' };
  }
  return { valid: true };
}

/**
 * Detect SQL injection patterns
 */
export function hasSQLInjection(input: string): boolean {
  const sqlPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/i,
    /(--|;|\/\*|\*\/)/,
    /(\bOR\b\s+\d+\s*=\s*\d+)/i,
    /(\bAND\b\s+\d+\s*=\s*\d+)/i,
    /('.*'.*=.*')/,
  ];
  return sqlPatterns.some((pattern) => pattern.test(input));
}

/**
 * Detect XSS patterns
 */
export function hasXSS(input: string): boolean {
  const xssPatterns = [
    /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
    /javascript\s*:/gi,
    /on\w+\s*=\s*["'][^"']*["']/gi,
    /<iframe[\s\S]*?>/gi,
    /<object[\s\S]*?>/gi,
    /eval\s*\(/gi,
    /expression\s*\(/gi,
  ];
  return xssPatterns.some((pattern) => pattern.test(input));
}

/**
 * Sanitize and validate a complete form object
 * Returns sanitized data and any validation errors
 */
export function sanitizeFormData<T extends Record<string, string>>(
  data: T
): { sanitized: T; errors: Partial<Record<keyof T, string>> } {
  const sanitized = { ...data } as T;
  const errors: Partial<Record<keyof T, string>> = {};

  for (const key in data) {
    const value = data[key];
    if (typeof value !== 'string') continue;

    // Check for injection attacks
    if (hasSQLInjection(value)) {
      errors[key] = 'Invalid characters detected.';
      sanitized[key] = '' as T[typeof key];
      continue;
    }
    if (hasXSS(value)) {
      errors[key] = 'Invalid content detected.';
      sanitized[key] = '' as T[typeof key];
      continue;
    }

    // Sanitize based on field name hints
    if (key.toLowerCase().includes('email')) {
      sanitized[key] = sanitizeEmail(value) as T[typeof key];
      if (sanitized[key] && !isValidEmail(sanitized[key] as string)) {
        errors[key] = 'Please enter a valid email address.';
      }
    } else if (key.toLowerCase().includes('phone')) {
      sanitized[key] = sanitizePhone(value) as T[typeof key];
    } else if (key.toLowerCase().includes('url') || key.toLowerCase().includes('website')) {
      sanitized[key] = sanitizeUrl(value) as T[typeof key];
    } else {
      sanitized[key] = sanitizeText(value) as T[typeof key];
    }
  }

  return { sanitized, errors };
}
